import { mediaUrl, TRANSITIONS } from "@/lib/api";
import { Poster } from "@/components/PosterCanvas";
import { HoverPreview } from "@/components/HoverPreview";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { NumberStepper } from "@/components/NumberStepper";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { CategorySelect, CategoryDot, findCategory } from "@/components/categories";
import { GripVertical, Lock, Unlock, Clock, Wand2, Trash2 } from "lucide-react";

const Thumb = ({ img, large }) => {
  if (img.type === "poster") {
    return large
      ? <Poster data={img.poster_data} className="h-full w-full" />
      : <div className="h-28 w-48 shrink-0 cursor-zoom-in overflow-hidden rounded-lg bg-black"><Poster data={img.poster_data} className="h-full w-full" /></div>;
  }
  const cls = large ? "h-full w-full object-contain" : "h-28 w-48 shrink-0 cursor-zoom-in rounded-lg object-cover";
  return <img src={mediaUrl(img.url)} alt="" className={cls} draggable={false} />;
};

const DurationField = ({ icon: Icon, label, value, onChange, disabled, testId, ...rest }) => (
  <div className="flex flex-wrap items-center justify-between gap-2">
    <Label className="flex items-center gap-1.5 text-xs"><Icon className="h-3.5 w-3.5" /> {label}</Label>
    <NumberStepper value={value} onChange={onChange} disabled={disabled} testId={testId} {...rest} />
  </div>
);

const RowActions = ({ img, idx, isPrincipal, canEdit, updateImage, removeImage }) => (
  <>
    {isPrincipal && (
      <Button size="icon" variant={img.locked ? "default" : "ghost"} className={img.locked ? "" : "text-muted-foreground"}
        onClick={() => updateImage(img.id, { locked: !img.locked })}
        title={img.locked ? "Déverrouiller" : "Verrouiller (bloquer pour l'admin secondaire)"}
        data-testid={`lock-image-button-${idx}`}>
        {img.locked ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
      </Button>
    )}
    {canEdit ? (
      <Button size="icon" variant="ghost" className="text-destructive hover:text-destructive"
        onClick={() => removeImage(img.id)} data-testid={`remove-image-button-${idx}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    ) : (
      <span className="flex items-center gap-1 whitespace-nowrap text-xs text-amber-500" data-testid={`locked-indicator-${idx}`}>
        <Lock className="h-3.5 w-3.5" /> Verrouillée
      </span>
    )}
  </>
);

const rowBorder = (img, idx, dragIndex, overIndex) => {
  if (overIndex === idx && dragIndex !== null && dragIndex !== idx) return "border-primary ring-1 ring-primary";
  return img.locked ? "border-amber-500/50" : "border-border";
};

export const BannerImageRow = ({ img, idx, categories, selected, onToggle, isPrincipal, dragIndex, overIndex, setDragIndex, setOverIndex, onDrop, updateImage, removeImage }) => {
  const canEdit = isPrincipal || !img.locked;
  const category = findCategory(categories, img.category_id);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setOverIndex(idx); }}
      onDrop={(e) => { e.preventDefault(); onDrop(idx); }}
      className={`flex flex-col gap-4 rounded-2xl border bg-card p-3 transition-colors sm:flex-row ${rowBorder(img, idx, dragIndex, overIndex)} ${dragIndex === idx ? "opacity-40" : ""}`}
      data-testid={`image-row-${idx}`}
    >
      <div className="flex items-center gap-3">
        <Checkbox checked={selected} onCheckedChange={() => onToggle(img.id)} disabled={!canEdit}
          title="Sélectionner pour le classement rapide" data-testid={`image-select-${idx}`} />
        <div draggable onDragStart={() => setDragIndex(idx)} onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
          className="flex cursor-grab flex-col items-center gap-1 text-muted-foreground transition-colors hover:text-primary active:cursor-grabbing"
          title="Glisser pour réordonner" data-testid={`drag-handle-${idx}`}>
          <GripVertical className="h-5 w-5" />
          <span className="text-center font-mono-label text-xs">{idx + 1}</span>
          {img.locked && <Lock className="h-3.5 w-3.5 text-amber-500" />}
        </div>
        <div className="relative">
          <HoverPreview testId={`image-preview-${idx}`} preview={<Thumb img={img} large />}>
            <Thumb img={img} />
          </HoverPreview>
          {category ? <div className="pointer-events-none absolute bottom-1 left-1"><CategoryDot category={category} testId={`image-category-badge-${idx}`} /></div>
            : <span className="pointer-events-none absolute bottom-1 left-1 rounded-md bg-amber-500 px-1.5 py-0.5 text-[10px] font-bold text-black" data-testid={`image-no-category-badge-${idx}`}>Sans catégorie</span>}
        </div>
      </div>

      <div className="flex-1 space-y-3">
        <DurationField icon={Clock} label="Durée d'affichage" value={img.duration} min={1} max={600} step={1}
          onChange={(v) => updateImage(img.id, { duration: v })} disabled={!canEdit} testId={`image-duration-input-${idx}`} />
        <DurationField icon={Wand2} label="Durée de transition" value={img.transition_duration ?? 0.8} min={0.1} max={10} step={0.1} decimals={1}
          onChange={(v) => updateImage(img.id, { transition_duration: v })} disabled={!canEdit} testId={`image-transition-duration-input-${idx}`} />
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-[140px] flex-1 space-y-1.5">
            <Label className="flex items-center gap-1.5 text-xs"><Wand2 className="h-3.5 w-3.5" /> Transition</Label>
            <Select value={img.transition} onValueChange={(v) => updateImage(img.id, { transition: v })} disabled={!canEdit}>
              <SelectTrigger data-testid={`image-transition-select-${idx}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRANSITIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <CategorySelect categories={categories} value={img.category_id} onChange={(v) => updateImage(img.id, { category_id: v })}
            disabled={!canEdit} testId={`image-category-select-${idx}`} />
          <RowActions img={img} idx={idx} isPrincipal={isPrincipal} canEdit={canEdit} updateImage={updateImage} removeImage={removeImage} />
        </div>
      </div>
    </div>
  );
};
